import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import {
    Form, Nav, OverlayTrigger, Popover,
} from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { FiHelpCircle } from "react-icons/fi";
import { Loading } from "../../../../components";
import CodeBlock from "../../../../components/code-block/CodeBlock";
import Confirm from "../../../../components/confirm/Confirm";
import Editor from "../../../../components/editor/Editor";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";
import useRouting, { Link, useParams } from "../../../../contexts/Routing";
import {
    ChatSettings,
    ChatSettingsInput, GetChannelInvitesDocument, Settings, useDeleteChannelInviteMutation,
    useGetAdminChannelInviteQuery, useGetChannelInvitesQuery, useSaveChannelInviteMutation,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";
import { SettingsProps } from "../base";
import { DisplayChatSettings } from "../chat/Chat";
import Styles from "./ChannelInvites.module.scss";
import useArticleContext from "../../../../contexts/HelpContext";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";
import PageActions from "../../../../components/page-actions/PageActions";

function ChannelVariablesHelp() {
    const popover = (
        <Popover className={Styles["popover-message"]}>
            <Popover.Header as="h3">Input Variables</Popover.Header>
            <Popover.Body>
                <p>
                    When displaying the Channel Creator, Foqal asks the user for their name, email, and company name.
                    When creating the channel, by entering the variables below you can customize the name of the channel
                    created and the introduction message displayed.
                </p>
                <p>
                    For example lets say you set your channel name template to
                    {" "}
                    <code>shared-&#123;&#123;COMPANY&#125;&#125;</code>
                    .
                    {" "}
                    When the user enters the company name &quot;Acme&quot;, the name of the channel created will be:
                    {" "}
                    <code>shared-acme</code>
                </p>
                <p>
                    You can use the following variables in the channel name template, the introduction message,
                    or in the body of Slack Block Kit Builder:
                </p>

                <ul>

                    <li>

                        <code>&#123;&#123;COMPANY&#125;&#125;</code>
                        {" "}
                        - Replaces with the name of the company entered.
                    </li>
                    <li>
                        <code>&#123;&#123;NAME&#125;&#125;</code>
                        {" "}
                        - Replaces with the full name that was entered.
                    </li>
                    <li>
                        <code>&#123;&#123;FIRST_NAME&#125;&#125;</code>
                        {" "}
                        - Replaces with the parsed first name entered.
                    </li>
                    <li>
                        <code>&#123;&#123;LAST_NAME&#125;&#125;</code>
                        {" "}
                        - Replaces with the parsed last name entered.
                    </li>
                    <li>
                        <code>&#123;&#123;EMAIL&#125;&#125;</code>
                        {" "}
                        - Replaces with the email address entered.
                    </li>

                </ul>
            </Popover.Body>
        </Popover>
    );
    return (
        <OverlayTrigger trigger="click" placement="bottom" overlay={popover}>
            <button type="button" className="btn btn-none p-0 text-default align-baseline">
                <FiHelpCircle />
                {" "}
                Available Variables
            </button>
        </OverlayTrigger>
    );
}

function isUrl(value?: string) {
    if (!value) {
        return false;
    }
    try {
        // eslint-disable-next-line no-new
        new URL(value);
        return true;
    } catch {
        return false;
    }
}

function createPreview(text: string) {
    return text
        .replace(/{{COMPANY}}/g, "Acme")
        .replace(/{{NAME}}/g, "Ava Nowak")
        .replace(/{{FIRST_NAME}}/g, "Ava")
        .replace(/{{LAST_NAME}}/g, "Nowak")
        .replace(/{{EMAIL}}/g, "ava@acme.com");
}
function DisplaySettings({
    id, onDeleted, onSave, settings,
}: {
    id: string;
    onDeleted: () => void;
    onSave: () => void;
    settings: Settings | undefined
}): ReactElement {
    useArticleContext("urn:fq:article:22c4892ab6ad48d5ac58d5a9f72559c4");

    const { data, error, loading: isLoading } = useGetAdminChannelInviteQuery({
        fetchPolicy: "no-cache",
        skip: id === "new",
        variables: {
            id,
        },
    });
    const [save] = useSaveChannelInviteMutation();
    const [deleteInvite] = useDeleteChannelInviteMutation();

    const [chatSettings, setChatSettings] = useState<ChatSettingsInput>();

    const isIntroUrl = isUrl(data?.channelInvite?.introductionTemplate ?? "");
    const [introductionTemplateUrl, setIntroductionTemplateUrl] = useState(
        isIntroUrl ? data?.channelInvite?.introductionTemplate ?? "" : "",
    );
    const [introductionTemplateBody, setIntroductionTemplateBody] = useState(
        !isIntroUrl ? data?.channelInvite?.introductionTemplate ?? "" : "",
    );
    const [channelNameTemplate, setChannelNameTemplate] = useState(data?.channelInvite?.channelNameTemplate);
    const [inviteName, setInviteName] = useState(data?.channelInvite?.name);
    const [description, setDescription] = useState(data?.channelInvite?.description);

    const [templateType, setTemplateType] = useState<string>("intro");

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Channel Template saved.",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    variables: {
                        id: id !== "new" ? id : undefined,
                        invite: {
                            channelNameTemplate: channelNameTemplate as string,
                            name: inviteName as string,
                            settings: chatSettings as ChatSettingsInput,
                            introductionTemplate: introductionTemplateUrl || introductionTemplateBody,
                            description: description as string,
                        },
                    },
                });
                onDone();
                await onSave();
            })();
        },
    });

    const isIntroductionUrl = isUrl(data?.channelInvite?.introductionTemplate ?? "");

    dirtyTracker.useWatchProperty(
        introductionTemplateUrl ?? "",
        isIntroductionUrl ? data?.channelInvite?.introductionTemplate ?? "" : "",
    );
    dirtyTracker.useWatchProperty(
        introductionTemplateBody ?? "",
        !isIntroductionUrl ? data?.channelInvite?.introductionTemplate ?? "" : "",
    );
    dirtyTracker.useWatchProperty(channelNameTemplate ?? "", data?.channelInvite?.channelNameTemplate ?? "");
    dirtyTracker.useWatchProperty(inviteName ?? "", data?.channelInvite?.name ?? "");
    dirtyTracker.useWatchProperty(description ?? "", data?.channelInvite?.description ?? "");
    dirtyTracker.useWatchProperty(templateType, isIntroductionUrl ? "block-kit" : "intro");

    dirtyTracker.useWatchDeepCompareProperty(chatSettings, data?.channelInvite?.settings);

    dirtyTracker.useLoad(() => {
        setIntroductionTemplateUrl(isIntroductionUrl ? data?.channelInvite?.introductionTemplate ?? "" : "");
        setIntroductionTemplateBody(!isIntroductionUrl ? data?.channelInvite?.introductionTemplate ?? "" : "");
        setChannelNameTemplate(data?.channelInvite?.channelNameTemplate);
        setInviteName(data?.channelInvite?.name ?? "");
        setDescription(data?.channelInvite?.description);
        setTemplateType(isIntroductionUrl ? "block-kit" : "intro");
        setChatSettings(data?.channelInvite?.settings as ChatSettings);
    }, [data?.channelInvite]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const onDelete = async () => {
        await deleteInvite({
            refetchQueries: [
                { query: GetChannelInvitesDocument },
            ],
            variables: {
                id,
            },
        });
        await onDeleted();
    };

    return (
        <form>
            <div className="border p-3 rounded bg-white mb-3">
                <h5 className="mb-3 text-light">
                    Channel template
                </h5>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">Description</Form.Label>
                    <Form.Control
                        value={description as string}
                        onChange={evt => {
                            setDescription(evt.target.value);
                        }}
                        placeholder="This invite is used by Sales to get new leads."
                        required
                        as="textarea"
                    />
                    <Form.Text>
                        The description of the invite useful to help understand what this invite is used for.
                        This description is not visibile to the user.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">Invite Name</Form.Label>
                    <Form.Control
                        value={inviteName as string}
                        onChange={evt => {
                            setInviteName(evt.target.value);
                        }}
                        placeholder="My Company"
                        required
                    />
                    <Form.Text>
                        This will usually be your company name and is the name that shows up in the channel template invite.
                        On the invite page, this says &quot;Slack Connect with
                        {" "}
                        {inviteName || "[Invite Name]"}
                        &quot;
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">Channel Name Template</Form.Label>
                    <Form.Control
                        value={channelNameTemplate as string}
                        onChange={evt => {
                            setChannelNameTemplate(evt.target.value);
                        }}
                        placeholder="shared-{{COMPANY}}"
                        required
                    />
                    <Form.Text>
                        The Channel Name template allows you to specify what the name of the channel will be when it
                        is created in Slack. Because the Channel Template Inviter will fail to create the
                        invite if the channel already exists, it is
                        recommended to use variables in the channel template. See
                        {" "}
                        <ChannelVariablesHelp />
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder d-block">
                        Introduction Template
                        {" "}
                        <small className="text-light">(Optional)</small>

                    </Form.Label>

                    <Form.Text>
                        After the customer fills out the Channel Template Inviter form, Foqal Agent can automatically
                        send a welcome message in the newly created channel. You can either enter a basic text
                        template or use the advanced Slack Block Kit builder to create a more interactive introduction.
                    </Form.Text>
                    <div className="d-flex mt-2">
                        <Nav
                            variant="pills"
                            defaultActiveKey="intro"
                            activeKey={templateType}
                            className="flex-column col-2 pe-3"
                        >
                            <Nav.Item>
                                <Nav.Link
                                    eventKey="intro"
                                    onClick={() => setTemplateType("intro")}
                                >
                                    Basic
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    eventKey="block-kit"
                                    onClick={() => setTemplateType("block-kit")}
                                >
                                    Advanced
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <div className="col">

                            {templateType === "block-kit" ? (
                                <div className="pt-2">
                                    <Form.Text className="mb-2 d-block">
                                        <p>
                                            To create an advanced welcome screen, you can use Slack Block Kit builder.
                                            This is a tool created by Slack to help you visualize an interactive
                                            Slack message. See
                                            {" "}
                                            <a
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-default"
                                                href="https://www.foqal.io/landing/slack-connect/default"
                                            >
                                                our example here
                                            </a>
                                            {" "}
                                            for a good starting point. Once you are done, simply copy and paste the entire URL
                                            into the box below.
                                        </p>
                                        <p>
                                            You can customize this message with the information entered by the customer.
                                            See the
                                            {" "}
                                            <ChannelVariablesHelp />
                                        </p>

                                    </Form.Text>

                                    <Form.Control
                                        value={introductionTemplateUrl}
                                        onChange={evt => {
                                            setIntroductionTemplateUrl(evt.target.value);
                                        }}
                                        placeholder="https://app.slack.com/block-kit-builder/..."
                                        as="textarea"
                                        type="url"
                                        className={Styles["intro-url"]}
                                    />
                                </div>
                            ) : (
                                <div>
                                    <Form.Text className="mb-2 d-block">
                                        <p>
                                            Enter the welcome message you would like the customer to see as soon as
                                            they accept the Slack Connect Channel invite.

                                            You can customize this message with the information entered by the customer at the
                                            time they are filling out the form. See
                                            {" "}
                                            <ChannelVariablesHelp />
                                        </p>

                                    </Form.Text>
                                    <div className="bg-white border-light border rounded text-break">

                                        <Editor
                                            text={introductionTemplateBody}
                                            placeholder="Hey {{COMPANY}}, welcome to support. We are happy..."
                                            onChange={msg => {
                                                setIntroductionTemplateBody(msg);
                                            }}
                                            editorClassName="overflow-auto"
                                            className={Styles["intro-message"]}
                                            isScrollDisabled
                                        />
                                    </div>
                                    {introductionTemplateBody && introductionTemplateBody !== "<p></p>" && (
                                        <div className="mt-3 text-break">
                                            <h5>Preview</h5>
                                            <small>
                                                The following preview is an example of what the introduction
                                                message will look like with a fake company and user.
                                            </small>
                                            <HtmlRenderer
                                                html={createPreview(introductionTemplateBody ?? "")}
                                                className="alert alert-info"
                                            />
                                        </div>
                                    )}

                                </div>
                            )}
                        </div>
                    </div>

                </Form.Group>

            </div>

            <DisplayChatSettings
                chatSettings={chatSettings as ChatSettings}
                settings={settings}
                onChange={setChatSettings}
                shouldAllowSlack
            />

            {id !== "new" && (
                <PageActions
                    className="mt-4"
                    onDelete={onDelete}
                    deleteConfirm={(
                        <div>
                            Are you sure you want to delete the channel template:
                            {" "}
                            <div className="mt-2 p-2 rounded alert alert-danger">
                                {channelNameTemplate}
                            </div>

                        </div>
                    )}
                />
            )}
            {dirtyTracker.saveComponent}

        </form>
    );
}

function DisplayList(): ReactElement {
    useArticleContext("urn:fq:article:22c4892ab6ad48d5ac58d5a9f72559c4");

    const { data, error, loading: isLoading } = useGetChannelInvitesQuery({
        fetchPolicy: "no-cache",
    });
    const { Paths } = useRouting();

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    return (
        <div className="d-flex align-items-stretch flex-column">
            <div className="border p-3 rounded bg-white">
                {data?.channelInvites?.isNotEmpty ? (
                    <>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>

                                <small className="text-gray-500">
                                    Channel templates allow your customers to create a Slack Connect Channel with your
                                    team without any work from your team or the IT department. By creating a Channel Template
                                    your customers can fill out a form and create a Slack Connect channel in your workspace.
                                </small>
                            </div>

                            <Link
                                to={Paths.ADMINISTRATOR_SETTING_ID}
                                params={{
                                    id: "new",
                                    setting: "channel-invites",
                                }}
                                className="btn btn-none btn-sm"
                            >
                                <AiOutlinePlus />
                                {" "}
                                Channel Template
                            </Link>

                        </div>
                        <div className="list-group">
                            {data?.channelInvites.sortBy(invite => invite.name.toLowerCase()).map(invite => (
                                <div
                                    className={"list-group-item list-group-item-action p-0 list-border "
                                    + "list-border-default mb-3 rounded"}
                                    key={invite.id}
                                >
                                    <Link
                                        to={Paths.ADMINISTRATOR_SETTING_ID}
                                        params={{
                                            id: invite.id,
                                            setting: "channel-invites",
                                        }}
                                        className="text-decoration-none d-block p-3"
                                    >
                                        <h5 className="m-0">
                                            {invite.description}
                                        </h5>
                                    </Link>
                                    <div className="mt-1">
                                        <small className="d-block">
                                            <CodeBlock
                                                size="sm"
                                                className="w-100 border-top"
                                                codeClassName="p-1"
                                                alert="gray-400"
                                                code={`https://${window.location.hostname}/channel-invite/${invite.id}`}
                                            />

                                        </small>
                                    </div>
                                </div>

                            ))}

                        </div>
                        <div className="d-flex justify-content-end align-items-center mt-3">
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING_ID}
                                params={{
                                    id: "new",
                                    setting: "channel-invites",
                                }}
                                className="btn btn-secondary btn-sm"

                            >
                                <AiOutlinePlus />
                                {" "}
                                Channel Template
                            </Link>
                        </div>

                    </>
                ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4 px-3">
                        <h5 className="text-light">
                            Channel Templates
                        </h5>
                        <small className="text-gray-500">
                            Channel Templates allow you to create a template that can be used to create channels and send
                            invites using Slack Connect with no manual work from your team. When you create a template, Foqal
                            will give you a unique web page you can send to your customers. When your customer
                            visits the page, they will be prompted to enter their name, email, and company name. Foqal will
                            then create a channel, invite the Agent Bot, configure it with your specific channel settings,
                            and send an Slack Connect invite to your customer. All this happens automatically without any
                            help from your team or IT.
                        </small>
                        <Link
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            params={{
                                id: "new",
                                setting: "channel-invites",
                            }}
                            className="btn btn-secondary btn-sm my-3"
                        >
                            <AiOutlinePlus />
                            {" "}
                            Channel Template
                        </Link>
                    </div>

                )}

            </div>

        </div>
    );
}

export default function ChannelInvitesSettings(props: SettingsProps): ReactElement {
    const { Paths, historyPush } = useRouting();

    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    return id ? (
        <DisplaySettings
            id={id}
            settings={props.settings}
            onDeleted={() => {
                historyPush(Paths.ADMINISTRATOR_SETTING, {
                    setting: "channel-invites",
                });
            }}
            onSave={() => {
                historyPush(Paths.ADMINISTRATOR_SETTING, {
                    setting: "channel-invites",
                });
            }}
        />
    ) : <DisplayList />;
}
